import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import copy from 'copy-to-clipboard';
import { useSnackbar } from 'notistack';
import { isNilOrEmpty } from 'ramda-extension';

import { getMessage } from '@gef-ui/localization/messages';
import Copy from '@gef-ui/material-icons/Copy';

import { equalOrThreeDashes } from 'utils';

export const CardPreview = ({ title, text }) => {
	const textFieldRef = useRef(null);
	const [showTooltipHover, setShowTooltipHover] = useState(false);
	const { enqueueSnackbar } = useSnackbar();
	const isEmpty = isNilOrEmpty(text);

	const handleCopy = useCallback(() => {
		copy(text);
		enqueueSnackbar(getMessage('common:copied'), { variant: 'info' });
	}, [text, enqueueSnackbar]);

	const compareSize = useCallback(() => {
		const compare = textFieldRef.current.scrollWidth > textFieldRef.current.clientWidth;
		setShowTooltipHover(compare);
	}, []);

	// compare once and add resize listener on "componentDidMount"
	useEffect(() => {
		compareSize();
		window.addEventListener('resize', compareSize);

		return () => window.removeEventListener('resize', compareSize);
	}, [compareSize]);

	return (
		<Card sx={{ borderRadius: '5px', boxShadow: '0 0 13px 0 rgba(0,0,0,.24)' }}>
			<CardHeader
				sx={{
					py: 0,
					px: 1,
					backgroundColor: 'info.shades.50p',
					// needed because of typography noWrap
					'& .MuiCardHeader-content': {
						width: '100%',
					},
				}}
				title={
					<Stack
						direction="row"
						sx={{
							alignItems: 'center',
							justifyContent: 'space-between',
						}}
					>
						<Box
							sx={{
								color: 'common.white',
								width: '80%',
								display: 'flex',
								alignItems: 'center',
							}}
						>
							{title}
						</Box>
						<IconButton size="small" disabled={isEmpty} onClick={!isEmpty ? handleCopy : null}>
							<Copy sx={{ color: 'common.white' }} />
						</IconButton>
					</Stack>
				}
			/>
			<CardContent sx={{ p: 1 }}>
				<Tooltip title={text} interactive disableFocusListener disableHoverListener={!showTooltipHover}>
					<div
						style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'noWrap' }}
						ref={textFieldRef}
					>
						{equalOrThreeDashes(text)}
					</div>
				</Tooltip>
			</CardContent>
		</Card>
	);
};

CardPreview.propTypes = {
	text: PropTypes.string,
	title: PropTypes.string,
};

export default CardPreview;
